// 選択中オブジェクトに対して 8 方位リサイズハンドルと 1 つの回転ハンドルを描画する。
// ハンドルはオブジェクトと同じ回転を持つ選択ボックス内に配置し、ドラッグ中の DOM
// プレビューにも追従するよう各入力ハンドラから選択ボックスを直接更新する。

import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import type { CanvasObject } from "@refinement-canvas/domain";
import { isObjectPlacementTool, useCanvasStore } from "./store/canvas-store";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useConnectorDragStore } from "./store/connector-drag-store";
import { useCanvasOpDispatcherContext } from "./dispatcher-context";
import { useResizeHandle, type ResizeDirection } from "./input/use-resize-handle";
import { useRotationHandle } from "./input/use-rotation-handle";
import { keepsAspectRatio } from "../../lib/object-aspect";
import { resolveResizeMinSize } from "../../lib/shape-default-size";
import { findCanvasObjectElement } from "./canvas-object-element";

const HANDLE_SIZE = 10;
const ROTATION_HANDLE_OFFSET = 28;
// 画面上でこの幅/高さ（px）未満になった辺では、中点ハンドルを省略する（コーナーと重なって潰れるため）。
const EDGE_HANDLE_MIN_SCREEN_PX = 40;
// 画面上でこのサイズ（px）未満の図形では、全ハンドルを省略して選択枠のみを表示する
//（キャンバス縮小時に小さすぎるハンドルを描かない。要件どおり、拡大すれば再表示される）。
const ALL_HANDLES_MIN_SCREEN_PX = 16;

export function CanvasSelectionOverlay(): JSX.Element | null {
  const selectedIds = useCanvasStore((s) => s.selectedObjectIds);
  const tool = useCanvasStore((s) => s.tool);
  const objects = useCanvasDocStore((s) => s.objects);

  // 配置ツール（付箋 / 図形 / テキスト / スタンプ / コメント）とペン描画の選択中は、リサイズ / 回転
  // ハンドルを表示しない。これらのツールは新規オブジェクトの作成 / 描画が目的であり、選択中オブジェクトの
  // ハンドル上からドラッグを開始したときに、作成 / 描画ではなくリサイズ / 回転が始まってしまう競合を防ぐ
  // （ハンドルの pointerdown は stopPropagation するため、ステージ側の配置 / ペン描画開始へ届かない）。
  // 特にペン描画中は、オブジェクト本体は pointerEvents:none で描画を透過させるのに、ハンドルだけが
  // 反応してしまうと図形の上に描けなくなるため、ここで確実に隠す。
  if (isObjectPlacementTool(tool) || tool === "draw-pen") {
    return null;
  }

  if (selectedIds.length !== 1) return null;
  const id = selectedIds[0]!;
  const target = objects[id];
  if (!target) return null;
  // ペンストロークは現状、選択後の移動のみ対応し、リサイズ / 回転ハンドルは表示しない。
  if (target.type === "pen-stroke") return null;
  // コネクタ（接続矢印）は端点の図形に追従するため、リサイズ / 回転 / 接続ハンドルは表示しない。
  if (target.type === "connector") return null;
  // ロック中もリサイズ・回転ハンドルを表示しない（移動・編集も他のフックで抑止する）。
  if (target.locked) return null;

  return <SingleSelectionOverlay key={id} object={target} />;
}

interface SingleProps {
  readonly object: Exclude<CanvasObject, { type: "pen-stroke" }>;
}

function SingleSelectionOverlay({ object }: SingleProps): JSX.Element {
  const dispatcher = useCanvasOpDispatcherContext();
  const targetRef = useRef<HTMLElement | null>(null);
  const [, forceTick] = useState(0);
  // テキストは本文量により実描画高さが object.h（minHeight 運用）を超え得るため、実測値を保持する。
  // 他種別は object.h と実描画が一致するため測らず null のまま（object.h を使う）。
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
  // 選択 UI はコメントスレッド同様にズーム倍率へ依存しない画面上一定サイズにする。
  // 選択ボックス自体は stage 座標（object.x/y/w/h）で描いてオブジェクトへ追従させつつ、
  // ハンドル・回転ハンドルの見た目サイズ・枠線・オフセットだけを 1/zoom で補正して、
  // stage の scale(zoom) を打ち消し画面上で一定 px に見えるようにする。
  const zoom = useCanvasStore((s) => s.view.zoom);
  const k = 1 / zoom;
  // 選択枠・ハンドルの基準高さ。テキストは実描画高さ（measuredHeight）を優先し、本文が object.h を
  // 超えて伸びてもハンドルが実際の下端へ追従するようにする。他種別は object.h。
  const boxHeight = object.type === "text" && measuredHeight !== null ? measuredHeight : object.h;
  // 画面上のオブジェクトサイズ（px）。小さすぎるときはハンドルを省略する。
  const screenW = object.w * zoom;
  const screenH = boxHeight * zoom;
  const showAnyHandles =
    screenW >= ALL_HANDLES_MIN_SCREEN_PX && screenH >= ALL_HANDLES_MIN_SCREEN_PX;
  const showHorizontalEdges = screenW >= EDGE_HANDLE_MIN_SCREEN_PX; // n / s（上下辺の中点）
  const showVerticalEdges = screenH >= EDGE_HANDLE_MIN_SCREEN_PX; // e / w（左右辺の中点）

  useLayoutEffect(() => {
    const el = findCanvasObjectElement<HTMLElement>(object.id);
    if (targetRef.current !== el) {
      targetRef.current = el ?? null;
      forceTick((n) => n + 1);
    }
    // 直接 DOM プレビュー（ドラッグ / リサイズ中の left/top/width/height 直書き）の取り残しを
    // 自己修復する。過去版の「確定 op の取りこぼし」や異常中断でプレビュー値が要素に残ると、
    // React は仮想スタイルが不変な限り書き戻さないため、本体（枠線）とストア基準の選択枠・
    // つまみが乖離したままになる。選択オーバーレイの描画時（選択・ストア更新・ズーム等）に
    // ストア値と食い違う inline スタイルを検出したらストア値へ揃える。
    // 重要: 本オーバーレイは objects 辞書全体を購読しており、他オブジェクトの確定更新でも
    // 再レンダーされる（共同編集中は進行中の move / resize / rotate の最中にも起き得る）。
    // その瞬間に修復するとプレビューがストア値へ巻き戻ってしまうため、当該オブジェクトが
    // 直接 DOM プレビュー中（＝move / resize / rotate がコネクタ追従用のライブ枠を
    // useConnectorDragStore へ登録している間）は修復をスキップする（ライブ枠は
    // pointerup / lostpointercapture / 中断の後始末で必ず破棄されるため、取り残しの修復は
    // 次のオーバーレイ描画で改めて行われる）。
    const isDirectPreviewActive = Boolean(useConnectorDragStore.getState().frames[object.id]);
    if (el && !isDirectPreviewActive) {
      const desiredLeft = `${object.x}px`;
      const desiredTop = `${object.y}px`;
      const desiredWidth = `${object.w}px`;
      if (el.style.left !== desiredLeft) el.style.left = desiredLeft;
      if (el.style.top !== desiredTop) el.style.top = desiredTop;
      if (el.style.width !== desiredWidth) el.style.width = desiredWidth;
      if (object.type === "text") {
        // テキストは minHeight 運用（React は height を設定しない）。リサイズプレビューの
        // 取り残しで inline height が付いたままだと本文追随（minHeight）を壊すため除去する。
        if (el.style.height) el.style.height = "";
      } else {
        const desiredHeight = `${object.h}px`;
        if (el.style.height !== desiredHeight) el.style.height = desiredHeight;
      }
      // 回転プレビューの取り残しも同様に修復する（未回転オブジェクトは inline transform なし）。
      const desiredTransform = object.rotation ? `rotate(${object.rotation}deg)` : "";
      if ((el.style.transform || "") !== desiredTransform) el.style.transform = desiredTransform;
    }
    // テキストの実描画高さを実測して選択枠へ反映する。offsetHeight が変化したときのみ setState し、
    // 毎レンダー実行でも値が安定していれば再レンダーを起こさない（ループ防止）。
    if (object.type === "text" && el) {
      const h = el.offsetHeight;
      setMeasuredHeight((prev) => (prev === h ? prev : h));
    } else {
      setMeasuredHeight((prev) => (prev === null ? prev : null));
    }
  });

  const handleRefs: Record<ResizeDirection, RefObject<HTMLDivElement | null>> = {
    nw: useRef<HTMLDivElement | null>(null),
    n: useRef<HTMLDivElement | null>(null),
    ne: useRef<HTMLDivElement | null>(null),
    e: useRef<HTMLDivElement | null>(null),
    se: useRef<HTMLDivElement | null>(null),
    s: useRef<HTMLDivElement | null>(null),
    sw: useRef<HTMLDivElement | null>(null),
    w: useRef<HTMLDivElement | null>(null),
  };
  const rotationHandleRef = useRef<HTMLDivElement | null>(null);

  // 画像・動画・スタンプはアスペクト比固定。四隅ハンドルのみ表示し、四隅ドラッグでは
  // Shift 無しでも常に縦横比を維持する（辺ハンドルは描画しない）。
  const lockAspect = keepsAspectRatio(object);
  // セクションは回転非対応（サーバも rotation を strip する）。回転ハンドルを表示しない。
  const isSection = object.type === "section";
  const resizeMinSize = resolveResizeMinSize(object);

  const common = {
    objectId: object.id,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    // リサイズの基準高さも選択枠と同じ boxHeight にする（テキストで実描画高さ > object.h のとき、
    // ハンドルの見た目位置とリサイズ計算基準がずれて下辺ドラッグがジャンプするのを防ぐ）。
    initialH: boxHeight,
    initialRotation: object.rotation ?? 0,
    lockAspect,
    ...resizeMinSize,
    targetRef,
  } as const;
  useResizeHandle({ ...common, direction: "nw", handleRef: handleRefs.nw });
  useResizeHandle({ ...common, direction: "n", handleRef: handleRefs.n });
  useResizeHandle({ ...common, direction: "ne", handleRef: handleRefs.ne });
  useResizeHandle({ ...common, direction: "e", handleRef: handleRefs.e });
  useResizeHandle({ ...common, direction: "se", handleRef: handleRefs.se });
  useResizeHandle({ ...common, direction: "s", handleRef: handleRefs.s });
  useResizeHandle({ ...common, direction: "sw", handleRef: handleRefs.sw });
  useResizeHandle({ ...common, direction: "w", handleRef: handleRefs.w });
  useRotationHandle({
    handleRef: rotationHandleRef,
    objectId: object.id,
    dispatcher,
    centerX: object.x + object.w / 2,
    // 回転中心も boxHeight 基準（見た目の中心）にし、選択枠・テキスト本体・回転計算の支点を一致させる。
    centerY: object.y + boxHeight / 2,
    initialRotation: object.rotation ?? 0,
    targetRef,
  });

  const allHandlePositions: {
    dir: ResizeDirection;
    left: string;
    top: string;
    cursor: string;
  }[] = [
    { dir: "nw", left: "0%", top: "0%", cursor: "nwse-resize" },
    { dir: "n", left: "50%", top: "0%", cursor: "ns-resize" },
    { dir: "ne", left: "100%", top: "0%", cursor: "nesw-resize" },
    { dir: "e", left: "100%", top: "50%", cursor: "ew-resize" },
    { dir: "se", left: "100%", top: "100%", cursor: "nwse-resize" },
    { dir: "s", left: "50%", top: "100%", cursor: "ns-resize" },
    { dir: "sw", left: "0%", top: "100%", cursor: "nesw-resize" },
    { dir: "w", left: "0%", top: "50%", cursor: "ew-resize" },
  ];
  // アスペクト比固定のオブジェクトは四隅（斜め）ハンドルのみ「描画対象」にする（辺ハンドルは DOM を作らない）。
  // 辺ハンドルを描画しないことで、その方位の useResizeHandle は handleRef が null となり何もしない。
  const CORNER_DIRECTIONS: ReadonlySet<ResizeDirection> = new Set(["nw", "ne", "se", "sw"]);
  const handlePositions = lockAspect
    ? allHandlePositions.filter((p) => CORNER_DIRECTIONS.has(p.dir))
    : allHandlePositions;
  // 画面上サイズに応じてハンドルの表示/非表示を切り替える（縮小時は辺ハンドル→全ハンドルの順に隠す）。
  // 重要: 表示切替は「DOM の出し入れ」ではなく display の切替で行う。useResizeHandle / useRotationHandle は
  // handleRef.current の DOM ノードへ pointer リスナを貼るが、その effect 依存はズーム非依存のため、
  // ズームでしきい値をまたいでハンドル DOM を作り直すと新ノードへリスナが貼り直されず、リサイズ/回転が
  // 無反応になる（拡大して再表示したハンドルが動かない）。そこで描画対象のハンドルは常にマウントし続け、
  // 小さすぎるときは display:none で隠す（＝リスナは維持されたまま、当たり判定だけ消える）。
  const isDirectionVisible = (dir: ResizeDirection): boolean => {
    if (!showAnyHandles) return false;
    if (dir === "n" || dir === "s") return showHorizontalEdges;
    if (dir === "e" || dir === "w") return showVerticalEdges;
    return true; // 四隅は showAnyHandles を満たせば表示する
  };

  return (
    <div
      data-testid="canvas-selection-overlay"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: Number.MAX_SAFE_INTEGER,
      }}
    >
      <div
        data-testid="canvas-selection-box"
        style={{
          position: "absolute",
          left: `${object.x}px`,
          top: `${object.y}px`,
          width: `${object.w}px`,
          height: `${boxHeight}px`,
          transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
          transformOrigin: "center",
          pointerEvents: "none",
        }}
      >
        {handlePositions.map((p) => (
          <div
            key={p.dir}
            ref={handleRefs[p.dir]}
            data-testid={`selection-handle-${p.dir}`}
            style={{
              position: "absolute",
              left: p.left,
              top: p.top,
              // ズーム倍率を打ち消す 1/zoom 補正で、画面上は常に一定サイズ（≒HANDLE_SIZE px）にする。
              width: `${HANDLE_SIZE * k}px`,
              height: `${HANDLE_SIZE * k}px`,
              marginLeft: `${(-HANDLE_SIZE * k) / 2}px`,
              marginTop: `${(-HANDLE_SIZE * k) / 2}px`,
              background: "var(--color-surface)",
              border: `${2 * k}px solid var(--color-selection)`,
              borderRadius: `${2 * k}px`,
              cursor: p.cursor,
              // 小さすぎるときは display:none で隠す（DOM は保持し pointer リスナを維持する）。
              display: isDirectionVisible(p.dir) ? "block" : "none",
              pointerEvents: "auto",
              boxSizing: "border-box",
            }}
          />
        ))}
        {/* 回転ハンドルも DOM を出し入れせず、小さすぎるときは display:none で隠す（リスナ維持のため）。 */}
        <div
          ref={rotationHandleRef}
          data-testid="selection-handle-rotation"
          style={{
            position: "absolute",
            // 上辺中央から、右上の頂点の外側（斜め方向）へ移動する。
            // 接続矢印のドットは 4 辺の中点外側に出るため、回転ハンドルが上辺中央のドットと
            // 重なって矢印操作の邪魔にならないよう、頂点の斜め外へ逃がす（矢印は斜め辺から出さない）。
            // オフセット・サイズ・枠線は 1/zoom 補正で画面上一定にする。
            left: "100%",
            top: "0%",
            marginLeft: `${(ROTATION_HANDLE_OFFSET - HANDLE_SIZE / 2) * k}px`,
            marginTop: `${(-ROTATION_HANDLE_OFFSET - HANDLE_SIZE / 2) * k}px`,
            width: `${HANDLE_SIZE * k}px`,
            height: `${HANDLE_SIZE * k}px`,
            background: "var(--color-selection)",
            border: `${2 * k}px solid var(--color-surface)`,
            borderRadius: "50%",
            cursor: "grab",
            // セクションは回転非対応のため、回転ハンドル自体を表示しない（display:none で
            // リスナは維持しつつ当たり判定を消す。他ハンドルの表示切替と同じ方針）。
            display: showAnyHandles && !isSection ? "block" : "none",
            pointerEvents: "auto",
            boxSizing: "border-box",
          }}
          title="ドラッグで回転（Shift で 15° スナップ）"
        />
      </div>
    </div>
  );
}
